import { useEffect, useMemo, useState } from "react";
import omit from "lodash/fp/omit";
import classNames from "classnames";
import TableViewToggles, { type TableViewTogglesViewType } from "@rio-cloud/rio-uikit/TableViewToggles";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import useSorting from "@rio-cloud/rio-uikit/useSorting";
import useTableSelection from "@rio-cloud/rio-uikit/useTableSelection";
import LoadMoreButton from "@rio-cloud/rio-uikit/LoadMoreButton";
import Toolbar, { ColumnDetails, ColumnDetailsMap, ColumnLabel } from "./Toolbar";
import Table, { Vehicle } from "./Table";
import Spinner from "@rio-cloud/rio-uikit/Spinner";

const defaultColumnOrder = [
  "date",
  "amount",
  "segment",
  "customer",
  "contract",
  "vehicle",
  "registration",
  "category",
  "subcategory",
  "taxes",
];

const disabledColumns = [""];

const columnLabels: ColumnLabel = {
  date: "Data",
  amount: "Valor",
  segment: "Segmento",
  customer: "Cliente",
  contract: "Contrato",
  vehicle: "Veículo",
  registration: "Registro da Solicitação",
  category: "Categoria",
  subcategory: "Subcategoria",
  taxes: "Impostos",
};

const demoColumnsDetails: ColumnDetailsMap = {};

const sortKeyMap: Record<string, keyof Vehicle | [keyof Vehicle, keyof Vehicle]> = {
  date: "sortableDate",
  amount: "sortableAmount",
};

export type TableRecipesProps = {
  viewType: TableViewTogglesViewType;
  data: Vehicle[];
  startDate: string;
  endDate: string;
  totalData: number;
  handleStartDate: (date: string) => void;
  handleEndDate: (date: string) => void;
  onLoadMore: () => void;
  handleRefresh: () => void;
  handleDownload: () => void;
  handleCreate: () => void;
  handleDelete: (id: string) => void;
  onDeleteFile?: (id: string) => void;
  onGetFile?: (id: string) => void;
  handleEdit: (id: string) => void;
  handleObservation: (id: string) => void;
  isLoadingMore?: boolean;
  isLoadingTable?: boolean;
};

const TableRecipes = ({
  viewType: externalViewType,
  data,
  startDate,
  endDate,
  handleEndDate,
  handleStartDate,
  totalData,
  onLoadMore,
  handleDownload,
  handleRefresh,
  handleCreate,
  handleDelete,
  handleEdit,
  handleObservation,
  onDeleteFile,
  onGetFile,
  isLoadingMore = false,
  isLoadingTable = false,
}: TableRecipesProps) => {
  const [filter] = useState<TableFilterKey[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);

  const [viewType, setViewType] = useState(externalViewType || TableViewToggles.VIEW_TYPE_TABLE);

  const transformDate = (dateString: string) => {
    const [day, month, year] = dateString.split("/");
    return `${year}-${month}-${day}`;
  };

  const transformAmount = (amountString: string) => {
    // Remove "R$ ", pontos de milhar e troca vírgula por ponto
    const cleaned = amountString
      .replace(/R\$\s?/g, "")
      .replace(/\./g, "")
      .replace(",", ".");
    return parseFloat(cleaned) || 0;
  };

  const transformedData = data.map((item) => ({
    ...item,
    sortableDate: transformDate(item.date),
    sortableAmount: transformAmount(item.amount),
  }));

  const { sortedItems, sortKey, sortDirection, setSortKey, setSortDirection, toggleDirection } = useSorting(
    transformedData,
    "sortableDate",
    "asc"
  );

  const { tableRef, updateRowSelection, onActiveRowChange } = useTableSelection<Vehicle>({
    tableData: data,
    idKey: "id" as keyof Vehicle,
  });

  useEffect(() => updateRowSelection(), [searchValue, updateRowSelection]);

  const handleToggleTableSettingsDialog = () => setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) => setViewType(newViewType);
  const handleSearchValueChange = (newSearchValue: string) => setSearchValue(newSearchValue);

  const handleColumnChange = (
    newColumnOrder: string[],
    newHiddenColumns: string[],
    newColumnsDetails = columnsDetails
  ) => {
    setColumnOrder(newColumnOrder);
    setHiddenColumns(newHiddenColumns);
    setColumnsDetails(newColumnsDetails);
  };

  // For immediate effect
  const handleColumnDetailsChange = (column: string, newColumnDetails: ColumnDetails) => {
    const updatedColumnsDetails = { ...columnsDetails };
    updatedColumnsDetails[column] = newColumnDetails;
    setColumnsDetails(updatedColumnsDetails);
  };

  const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");

    if (newSortBy) {
      const newSortKey = sortKeyMap[newSortBy] || newSortBy;

      setSortKey(newSortKey as keyof Vehicle);

      if (newSortKey === sortKey) {
        toggleDirection();
      }
    }
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    setSortKey(newSortBy as keyof Vehicle);
    setSortDirection(newSortDir);
  };

  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  const withoutHidden = omit(hiddenColumns);
  const filteredRows = sortedItems.map((vehicle) => ({ ...withoutHidden(vehicle) })) as Vehicle[];

  const searchResult = useMemo(() => {
    if (!searchValue) {
      return filteredRows;
    }
    return filteredRows.filter((row: Partial<Vehicle>) =>
      columns.some((col) => {
        const value = row[col as keyof Vehicle];
        if (value == null) {
          return false;
        }
        return String(value).toLowerCase().includes(searchValue.toLowerCase());
      })
    );
  }, [searchValue, filteredRows, columns]);

  // Filter according to filter dialog settings
  const rows = useMemo(() => applyFilter(searchResult, filter), [searchResult, filter]);

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    // 'table-column-overflow-hidden',
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortKey,
      disabled: false,
    };
  });

  const loadMoreMessage = isLoadingMore ? (
    <span className="display-flex align-items-center">
      <span className="margin-right-10">
        <Spinner isInline />
      </span>
      Carregando...
    </span>
  ) : (
    "Carregar mais"
  );

  return (
    <div className="height-100pct">
      <Toolbar
        handleCreate={handleCreate}
        searchValue={searchValue}
        handleSearchValueChange={handleSearchValueChange}
        viewType={viewType}
        handleViewTypeChange={handleViewTypeChange}
        handleToggleTableSettingsDialog={handleToggleTableSettingsDialog}
        showTableSettingsDialog={showTableSettingsDialog}
        handleColumnChange={handleColumnChange}
        defaultColumnOrder={defaultColumnOrder}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        columnLabels={columnLabels}
        disabledColumns={disabledColumns}
        columnsDetails={columnsDetails}
        handleColumnDetailsChange={handleColumnDetailsChange}
        startDate={startDate}
        handleStartDate={handleStartDate}
        endDate={endDate}
        handleEndDate={handleEndDate}
        handleDownload={handleDownload}
        handleRefresh={handleRefresh}
      />
      {sortKey && !isViewTypeTable && (
        <TableCardsSorting
          selectOptions={cardSortingSelectOptions}
          sortName={sortKey as string}
          sortOrder={sortDirection}
          onSortChange={handleCardSortChange}
        />
      )}
      <Table
        rows={rows}
        isViewTypeTable={isViewTypeTable}
        tableRef={tableRef}
        tableClassNames={tableClassNames}
        columns={columns}
        columnsDetails={columnsDetails}
        columnLabels={columnLabels}
        sortKey={sortKey}
        sortDirection={sortDirection}
        handleSortChange={handleSortChange}
        onActiveRowChange={onActiveRowChange}
        handleDelete={handleDelete}
        onDeleteFile={onDeleteFile}
        onGetFile={onGetFile}
        handleEdit={handleEdit}
        handleObservation={handleObservation}
        isLoading={isLoadingTable}
      />

      {rows?.length > 0 ? (
        <LoadMoreButton
          loaded={rows.length}
          total={totalData}
          onLoadMore={onLoadMore}
          loadMoreMessage={loadMoreMessage}
          isInteractive={!isLoadingMore}
          noMoreMessage="Não há mais resultado"
        />
      ) : null}
    </div>
  );
};

const FILTER_KAY_MISSING_VIN = "missingVin";
type TableFilterKey = typeof FILTER_KAY_MISSING_VIN;

const applyFilter = (items: Vehicle[], filter: string[]) => {
  const filterSet = new Set(filter);

  return items.filter((item) => {
    if (filterSet.has(FILTER_KAY_MISSING_VIN) && !item.date) {
      return false;
    }

    return true;
  });
};

export default TableRecipes;
